import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Link, useNavigate } from "@tanstack/react-router";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Calendar,
  Compass,
  LayoutDashboard,
  LogOut,
  Menu,
  Search,
  User,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useCurrentUser, useMyBookings, useSignOut } from "@/api/hooks";
import type { User as AccountUser } from "@/api/types";
import { logo } from "@/assets";
import { ConsumerHomeLink } from "@/components/layout/consumer-home-link";
import { Button } from "@/components/ui/button";
import {
  BookingsNavCalendarIcon,
  BookingsUpcomingCountBadge,
} from "@/features/bookings/bookings-nav-calendar-icon";
import { formatBookingsNavLabel, getUpcomingBookingsCount } from "@/features/bookings/utils";
import { useMediaQuery } from "@/hooks/use-media-query";
import { extractApiError } from "@/lib/extract-api-error";

interface ConsumerHeaderProps {
  headerContent?: ReactNode;
  compactNav?: boolean;
  transparent?: boolean;
  mobileSearchOnly?: boolean;
  mobileLeftAction?: ReactNode;
  mobileRightAction?: ReactNode;
}

// fallow-ignore-next-line complexity
function ConsumerHeader({
  headerContent,
  compactNav,
  transparent,
  mobileSearchOnly,
  mobileLeftAction,
  mobileRightAction,
}: ConsumerHeaderProps) {
  const { data: user } = useCurrentUser();
  const { data: upcomingCount = 0 } = useMyBookings({
    enabled: !!user,
    placeholderData: (previous) => previous,
    select: (data) => getUpcomingBookingsCount(data.upcoming, data.past),
  });
  const isDesktopNav = useMediaQuery("(min-width: 880px)");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const displayLabel =
    user?.display_name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    user?.email ||
    "";
  const initials = user ? displayLabel.slice(0, 2).toUpperCase() : "";

  const isMobileSearchMode = mobileSearchOnly && !isDesktopNav;

  const [scrollProgress, setScrollProgress] = useState(0);
  useEffect(() => {
    if (!transparent) return;
    const onScroll = () => {
      setScrollProgress(Math.min(window.scrollY / 100, 1));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparent]);

  const frostedBg = transparent
    ? `rgba(250,250,247,${(0.92 * scrollProgress).toFixed(2)})`
    : "rgba(250,250,247,0.92)";
  const frostedBlur = transparent
    ? `saturate(1.2) blur(${12 * scrollProgress}px)`
    : "saturate(1.2) blur(12px)";

  return (
    <>
      <header
        className={`sticky top-0 z-30 ${!transparent || scrollProgress > 0.5 ? "border-b border-fog" : ""}`}
        style={{
          background: frostedBg,
          backdropFilter: frostedBlur,
          WebkitBackdropFilter: frostedBlur,
          borderColor: transparent ? `rgba(239,239,236,${scrollProgress})` : undefined,
        }}
      >
        {isMobileSearchMode ? (
          <div className="flex items-center gap-2 px-3 py-2.5">
            {mobileLeftAction}
            <div className="min-w-0 flex-1">{headerContent}</div>
            {mobileRightAction}
          </div>
        ) : (
          <div
            className={
              compactNav
                ? "relative flex items-center gap-4 px-6 py-3.5"
                : "relative mx-auto flex max-w-[1200px] items-center justify-between gap-4 px-5 py-3.5"
            }
          >
            <HeaderLogo />

            {headerContent &&
              (compactNav ? (
                <div className="hidden min-w-0 flex-1 md:block">
                  <div className="mx-auto max-w-[680px]">{headerContent}</div>
                </div>
              ) : (
                <div className="hidden flex-1 md:block">{headerContent}</div>
              ))}

            {isDesktopNav && !compactNav && !headerContent && (
              <nav className="ml-auto flex items-center gap-7" aria-label="Primary">
                <NavLink to="/search">Discover</NavLink>
                <NavLink to="/for-pros">For pros</NavLink>
              </nav>
            )}

            <div className="flex shrink-0 items-center gap-2">
              {user && (
                <>
                  <IconButton
                    label={formatBookingsNavLabel(upcomingCount)}
                    as="link"
                    to="/bookings"
                  >
                    <BookingsNavCalendarIcon count={upcomingCount} />
                  </IconButton>
                  <AvatarPill
                    initials={initials}
                    firstName={user.display_name || user.first_name}
                    colorSeed={user.id}
                    onClick={() => setDrawerOpen(true)}
                  />
                </>
              )}

              {isDesktopNav && !user && (
                <Button variant="secondary" size="default" asChild>
                  <Link to="/auth/sign-in">Log in</Link>
                </Button>
              )}

              {!isDesktopNav && !user && (
                <IconButton label="Menu" onClick={() => setDrawerOpen(true)}>
                  <Menu className="size-[22px]" strokeWidth={2} />
                </IconButton>
              )}
            </div>
          </div>
        )}
      </header>

      <NavDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        user={user}
        initials={initials}
        isDesktop={isDesktopNav}
        upcomingCount={upcomingCount}
      />
    </>
  );
}

function HeaderLogo() {
  return (
    <ConsumerHomeLink
      className="flex shrink-0 items-center gap-2.5 text-ink no-underline"
      aria-label="Slotique"
    >
      <img src={logo} alt="" className="block size-10 object-contain" />
      <span className="font-display text-[22px] font-semibold leading-none tracking-tight">
        Slotique
      </span>
    </ConsumerHomeLink>
  );
}

function NavLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="group relative py-2 text-[14px] font-medium text-ink no-underline">
      {children}
      <span className="absolute inset-x-0 -bottom-0.5 h-0.5 origin-left scale-x-0 bg-electric transition-transform duration-short ease-default group-hover:scale-x-100" />
    </Link>
  );
}

function IconButton({
  label,
  children,
  onClick,
  as,
  to,
}: {
  label: string;
  children: ReactNode;
  onClick?: () => void;
  as?: "link";
  to?: string;
}) {
  const cls =
    "relative inline-flex size-10 items-center justify-center rounded-full border-none bg-transparent text-ink transition-colors duration-short ease-default hover:bg-fog active:bg-fog cursor-pointer";

  if (as === "link" && to) {
    return (
      <Link to={to} className={cls} aria-label={label}>
        {children}
      </Link>
    );
  }

  return (
    <button type="button" className={cls} aria-label={label} onClick={onClick}>
      {children}
    </button>
  );
}

const AVATAR_COLORS = [
  "#3B7A57",
  "#5B6ABF",
  "#BF6340",
  "#2E86AB",
  "#7B4B94",
  "#4A7C59",
  "#A0522D",
  "#6B5B95",
];

function getAvatarColor(seed: string): string {
  let hash = 0;
  for (const char of seed) hash = ((hash << 5) - hash + char.charCodeAt(0)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length] ?? "#6B5B95";
}

function AvatarPill({
  initials,
  firstName,
  colorSeed,
  onClick,
}: {
  initials: string;
  firstName?: string | null;
  colorSeed: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-pill bg-transparent pl-1 pr-3.5 py-1 font-inherit text-ink transition-colors duration-short ease-default hover:bg-electric-50 hover:text-electric-800"
      onClick={onClick}
      aria-label="Menu"
    >
      <span
        className="flex size-8 items-center justify-center rounded-full text-[12px] font-bold text-white"
        style={{ background: getAvatarColor(colorSeed) }}
      >
        {initials || <User className="size-4" aria-hidden="true" />}
      </span>
      {firstName && (
        <span className="hidden max-w-[100px] truncate text-[13px] font-semibold sm:inline">
          {firstName}
        </span>
      )}
      <Menu className="size-4 opacity-70" strokeWidth={2} />
    </button>
  );
}

// fallow-ignore-next-line complexity
function NavDrawer({
  open,
  onClose,
  user,
  initials,
  isDesktop,
  upcomingCount,
}: {
  open: boolean;
  onClose: () => void;
  user: AccountUser | null | undefined;
  initials: string;
  isDesktop: boolean;
  upcomingCount: number;
}) {
  const navigate = useNavigate();
  const signOut = useSignOut(() => {
    onClose();
    void navigate({ to: "/" });
  });
  const reducedMotion = useReducedMotion();
  const previousFocusRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const fullName = user
    ? user.display_name ||
      [user.first_name, user.last_name].filter(Boolean).join(" ") ||
      user.email ||
      user.mobile_number ||
      "Account"
    : "";

  const panelCls = isDesktop
    ? "fixed inset-y-0 right-0 z-[60] flex w-[min(360px,86vw)] flex-col bg-paper shadow-xl"
    : "fixed inset-0 z-[60] flex flex-col bg-paper";

  const content = (
    <motion.aside
      className={panelCls}
      initial={{ x: isDesktop ? "100%" : 0, y: isDesktop ? 0 : "100%" }}
      animate={{ x: 0, y: 0 }}
      exit={{ x: isDesktop ? "100%" : 0, y: isDesktop ? 0 : "100%" }}
      transition={{ type: "tween", duration: reducedMotion ? 0 : 0.2, ease: "easeOut" }}
    >
      <DialogPrimitive.Title className="sr-only">Menu</DialogPrimitive.Title>
      <div className="flex items-center justify-end px-5 py-3">
        <IconButton label="Close menu" onClick={onClose}>
          <X className="size-6" strokeWidth={2} />
        </IconButton>
      </div>

      {user && (
        <div className="flex items-center gap-3.5 border-b border-fog px-6 py-5">
          <span
            className="flex size-14 shrink-0 items-center justify-center rounded-full text-[20px] font-bold text-white"
            style={{ background: getAvatarColor(user.id) }}
          >
            {initials || <User className="size-6" aria-hidden="true" />}
          </span>
          <div className="flex flex-col">
            <span className="text-[16px] font-semibold">{fullName}</span>
            {user.email && <span className="text-[13px] text-slate">{user.email}</span>}
          </div>
        </div>
      )}

      <nav
        className="flex flex-1 flex-col gap-1 overflow-y-auto overscroll-y-contain px-6 py-5"
        aria-label="Menu"
      >
        {!user && (
          <span className="px-2 pb-1 text-[12px] font-bold uppercase tracking-[0.08em] text-slate">
            For customers
          </span>
        )}

        {user ? (
          <>
            <DrawerNavLink
              icon={<Calendar className="size-[22px]" strokeWidth={1.8} />}
              trailing={<BookingsUpcomingCountBadge count={upcomingCount} />}
              ariaLabel={formatBookingsNavLabel(upcomingCount)}
              to="/bookings"
              onClick={onClose}
            >
              My bookings
            </DrawerNavLink>
            <DrawerNavLink icon={<User />} to="/account" onClick={onClose}>
              Profile &amp; settings
            </DrawerNavLink>
            {user.has_business && (
              <DrawerNavLink
                icon={<LayoutDashboard className="size-[22px]" strokeWidth={1.8} />}
                to="/dashboard"
                onClick={onClose}
              >
                Pro dashboard
              </DrawerNavLink>
            )}
            <DrawerNavLink
              icon={<Compass className="size-[22px]" strokeWidth={1.8} />}
              to={user.has_business ? "/onboarding/new" : "/onboarding/start"}
              onClick={onClose}
            >
              List your business
            </DrawerNavLink>
          </>
        ) : (
          <DrawerNavExternalLink
            icon={<LogInIcon />}
            to="/auth/sign-in"
            onClick={onClose}
            highlight
          >
            Log in or sign up
          </DrawerNavExternalLink>
        )}

        {!isDesktop && (
          <DrawerNavLink icon={<Search />} to="/search" onClick={onClose}>
            Discover salons
          </DrawerNavLink>
        )}

        {(!isDesktop || !user) && (
          <>
            <span className="mx-2 my-3 block h-px bg-fog" />

            <Link
              to="/for-pros"
              className="group flex items-center justify-between rounded-sm px-2 py-3.5 text-[17px] font-semibold text-ink no-underline transition-colors hover:bg-ink hover:text-lime active:bg-ink active:text-lime"
              onClick={onClose}
            >
              <span className="flex items-center gap-3.5">
                <Compass
                  className="size-[22px] text-slate transition-colors group-hover:text-lime group-active:text-lime"
                  strokeWidth={1.8}
                />
                Slotique for Professionals
              </span>
              <ArrowRight
                className="size-[18px] text-slate transition-colors group-hover:text-lime group-active:text-lime"
                strokeWidth={2}
              />
            </Link>
          </>
        )}
      </nav>

      <div className="mt-auto flex flex-col gap-2.5 p-6">
        {user && signOut.isError && (
          <p role="alert" className="text-[14px] text-danger">
            {extractApiError(signOut.error, "Could not sign out. Please try again.")}
          </p>
        )}
        {user ? (
          <Button
            variant="ghost"
            className="w-full active:bg-fog-dark"
            loading={signOut.isPending}
            onClick={() => {
              if (signOut.isPending) return;
              signOut.mutate();
            }}
          >
            <LogOut className="size-4" />
            Sign out
          </Button>
        ) : (
          !isDesktop && (
            <Button variant="primary" className="w-full h-12 text-[15px]" asChild>
              <Link to="/auth/sign-in" onClick={onClose}>
                Sign in
              </Link>
            </Button>
          )
        )}
      </div>
    </motion.aside>
  );

  return (
    <AnimatePresence>
      {open && (
        <DialogPrimitive.Root open onOpenChange={(nextOpen) => !nextOpen && onClose()}>
          <DialogPrimitive.Portal>
            {isDesktop && (
              <motion.div
                className="pointer-events-auto fixed inset-0 z-50 bg-black/40"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: reducedMotion ? 0 : 0.2 }}
                onClick={onClose}
                aria-hidden="true"
              />
            )}
            <DialogPrimitive.Content
              asChild
              aria-modal="true"
              aria-describedby={undefined}
              onOpenAutoFocus={() => {
                previousFocusRef.current = document.activeElement;
              }}
              onCloseAutoFocus={(event) => {
                event.preventDefault();
                if (previousFocusRef.current instanceof HTMLElement)
                  previousFocusRef.current.focus();
              }}
            >
              {content}
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      )}
    </AnimatePresence>
  );
}

function DrawerNavLink({
  icon,
  trailing,
  ariaLabel,
  to,
  href,
  onClick,
  children,
}: {
  icon: ReactNode;
  trailing?: ReactNode;
  ariaLabel?: string;
  to?: string;
  href?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  const cls =
    "flex items-center gap-3.5 rounded-sm px-2 py-3.5 text-[17px] font-medium text-ink no-underline transition-colors hover:bg-fog active:bg-fog";

  const content = (
    <>
      <span className="flex size-[22px] shrink-0 items-center justify-center">{icon}</span>
      <span className="min-w-0 flex-1">{children}</span>
      {trailing}
    </>
  );

  if (href) {
    return (
      <a href={href} className={cls} aria-label={ariaLabel} onClick={onClick}>
        {content}
      </a>
    );
  }

  return (
    <Link to={to ?? "/"} className={cls} aria-label={ariaLabel} onClick={onClick}>
      {content}
    </Link>
  );
}

function DrawerNavExternalLink({
  icon,
  to,
  onClick,
  highlight,
  children,
}: {
  icon: ReactNode;
  to: string;
  onClick: () => void;
  highlight?: boolean;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      className={`group flex items-center gap-3.5 rounded-sm px-2 py-3.5 text-[17px] font-semibold no-underline transition-colors ${highlight ? "text-electric-800 hover:bg-electric-50 active:bg-electric-50" : "text-ink hover:bg-fog active:bg-fog"}`}
      onClick={onClick}
    >
      <span
        className={`size-[22px] transition-colors ${highlight ? "text-electric-800" : "text-slate"}`}
      >
        {icon}
      </span>
      {children}
    </Link>
  );
}

function LogInIcon() {
  return (
    <svg
      className="size-[22px]"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 12H3M11 8l-4 4 4 4M21 4v16" />
    </svg>
  );
}

export { ConsumerHeader, type ConsumerHeaderProps };
